<template>
  <div :id="chartId" class="plotly-chart-container"></div>
</template>

<script>
import Plotly from 'plotly.js-basic-dist';

export default {
  name: 'PlotlyChart',
  props: {
    chartId: {
      type: String,
      required: true
    },
    datasets: {
      type: Array,
      required: true
    },
    chartTitle: {
      type: String,
      default: ''
    },
    dataUnit: {
      type: String,
      default: ''
    },
    yAxisMin: {
      type: Number,
      default: null
    },
    yAxisMax: {
      type: Number,
      default: null
    },
    hiddenLegends: {
      type: Array,
      default: () => []
    },
    isFilled: {
      type: Boolean,
      default: false
    },
    rotateXAxisLabels: {
      type: Boolean,
      default: false
    },
    xAxisTickAngle: {
      type: Number,
      default: 0
    },
    secondaryDatasets: {
      type: Array,
      default: () => []
    },
    secondaryDataUnit: {
      type: String,
      default: ''
    },
    yAxisType: {
      type: String,
      default: 'linear',
      validator: (value) => ['linear', 'log'].includes(value)
    },
    secondaryYAxisType: {
      type: String,
      default: 'linear',
      validator: (value) => ['linear', 'log'].includes(value)
    }
  },
  data() {
    return {
      plotlyInstance: null,
      eventHandlersSetup: false,
      darkModeObserver: null,
      hoverObserver: null,
      hoverEventHandler: null,
      legendClickHandler: null,
      legendDoubleClickHandler: null
    };
  },
  mounted() {
    this.createChart();
    // Watch for dark mode changes
    this.darkModeObserver = new MutationObserver(() => {
      if (this.plotlyInstance) {
        this.updateChart();
      }
    });
    this.darkModeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    // Handle window resize to ensure chart uses full width
    window.addEventListener('resize', this.handleResize);
    // Force resize after a short delay to ensure container is fully rendered
    this.$nextTick(() => {
      setTimeout(() => {
        if (this.plotlyInstance) {
          Plotly.Plots.resize(this.chartId);
        }
      }, 100);
    });
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
    
    // Remove all event handlers
    this.removeEventHandlers();
    
    if (this.plotlyInstance) {
      Plotly.purge(this.chartId);
      this.plotlyInstance = null;
    }
    if (this.darkModeObserver) {
      this.darkModeObserver.disconnect();
    }
  },
  watch: {
    datasets: {
      handler() {
        this.updateChart();
      },
      deep: true
    },
    hiddenLegends: {
      handler() {
        this.updateChart();
      },
      deep: true
    },
    xAxisTickAngle: {
      handler() {
        this.updateChart();
      }
    },
    rotateXAxisLabels: {
      handler() {
        this.updateChart();
      }
    },
    dataUnit: {
      handler() {
        this.updateChart();
      }
    },
    secondaryDatasets: {
      handler() {
        this.updateChart();
      },
      deep: true
    },
    secondaryDataUnit: {
      handler() {
        this.updateChart();
      }
    },
    yAxisType: {
      handler() {
        this.updateChart();
      }
    },
    secondaryYAxisType: {
      handler() {
        this.updateChart();
      }
    }
  },
  methods: {
    createChart() {
      if (!this.datasets || this.datasets.length === 0) {
        return;
      }

      const traces = this.convertToPlotlyTraces();
      const layout = this.createLayout();
      const config = {
        responsive: true,
        displayModeBar: true,
        displaylogo: false,
        modeBarButtonsToRemove: ['lasso2d', 'select2d'],
        useResizeHandler: true
      };

      Plotly.newPlot(this.chartId, traces, layout, config).then(() => {
        this.plotlyInstance = document.getElementById(this.chartId);
        this.setupEventHandlers();
        // Force chart to use full width after initial render
        this.$nextTick(() => {
          Plotly.Plots.resize(this.chartId);
          // Force x-axis to use full range without padding
          if (this.datasets.length > 0 && this.datasets[0].labels) {
            const labelCount = this.datasets[0].labels.length;
            if (labelCount > 0) {
              Plotly.relayout(this.chartId, {
                'xaxis.range': [0, labelCount - 1]
              });
            }
          }
        });
      });
    },
    updateChart() {
      if (!this.plotlyInstance) {
        this.createChart();
        return;
      }

      const traces = this.convertToPlotlyTraces();
      const layout = this.createLayout();
      
      Plotly.react(this.chartId, traces, layout).then(() => {
        // Reset flag and re-setup event handlers after chart update
        // This ensures handlers are reattached if Plotly cleared them
        this.eventHandlersSetup = false;
        this.setupEventHandlers();
        // Force resize and relayout to ensure full width usage and remove padding
        this.$nextTick(() => {
          Plotly.Plots.resize(this.chartId);
          // Force x-axis to use full range without padding
          if (this.datasets.length > 0 && this.datasets[0].labels) {
            const labelCount = this.datasets[0].labels.length;
            if (labelCount > 0) {
              Plotly.relayout(this.chartId, {
                'xaxis.range': [0, labelCount - 1]
              });
            }
          }
        });
      });
    },
    convertToPlotlyTraces() {
      const traces = [];
      const labels = this.datasets.length > 0 ? this.datasets[0].labels : [];

      // Add primary y-axis traces
      this.datasets.forEach((dataset, index) => {
        // Determine line style based on group
        const lastPart = dataset.group.split('_')[dataset.group.split('_').length - 1];
        let dash = 'solid';
        if (lastPart === 'actual') {
          dash = 'solid';
        } else if (lastPart === 'adjusted') {
          dash = 'dash';
        } else {
          dash = 'dot';
        }

        // Handle warning datasets (scatter)
        if (dataset.group === 'price_actual_warnings') {
          traces.push({
            x: labels,
            y: dataset.values,
            type: 'scatter',
            mode: 'markers',
            marker: {
              symbol: 'triangle-up',
              size: 8,
              color: dataset.color || 'rgba(255, 0, 0, 0.8)'
            },
            name: dataset.name,
            showlegend: !dataset.name.includes('Last Known'),
            visible: !this.hiddenLegends.includes(dataset.name) ? true : 'legendonly'
          });
        } else {
          // Regular line chart
          const trace = {
            x: labels,
            y: dataset.values,
            type: 'scatter',
            mode: 'lines',
            name: dataset.name,
            line: {
              color: dataset.color,
              dash: dash,
              width: 2.5
            },
            fill: this.isFilled ? 'tozeroy' : 'none',
            visible: !this.hiddenLegends.includes(dataset.name) ? true : 'legendonly'
          };

          traces.push(trace);
        }
      });

      // Add secondary y-axis traces (SoC)
      if (this.secondaryDatasets && this.secondaryDatasets.length > 0) {
        this.secondaryDatasets.forEach((dataset) => {
          const lastPart = dataset.group.split('_')[dataset.group.split('_').length - 1];
          let dash = 'solid';
          if (lastPart === 'actual') {
            dash = 'solid';
          }

          // Check if this is a SoC dataset and add shadow effect
          if (dataset.group === 'soc_actual') {
            // Use cyan shade for shadow (lighter cyan with reduced opacity)
            // This creates a subtle cyan glow/shadow effect
            const shadowColor = 'rgba(64, 224, 208, 0.35)';  // Cyan shadow

            // Determine visibility based on main trace visibility
            // Shadow should match the main trace visibility
            const mainTraceVisible = !this.hiddenLegends.includes(dataset.name) ? true : 'legendonly';

            // Create shadow trace (rendered first, behind main line)
            const shadowTrace = {
              x: labels,
              y: dataset.values,
              type: 'scatter',
              mode: 'lines',
              name: dataset.name + ' (shadow)',
              line: {
                color: shadowColor,
                dash: dash,
                width: 3.5
              },
              fill: 'none',
              visible: mainTraceVisible, // Match main trace visibility
              yaxis: 'y2',
              showlegend: false, // Don't show shadow in legend
              hoverinfo: 'skip' // Don't show shadow in hover tooltip
            };

            traces.push(shadowTrace);
          }

          // Create main trace (rendered on top)
          // Use same line width for all secondary datasets
          const lineWidth = 2.5;
          const trace = {
            x: labels,
            y: dataset.values,
            type: 'scatter',
            mode: 'lines',
            name: dataset.name,
            line: {
              color: dataset.color,
              dash: dash,
              width: lineWidth
            },
            fill: 'none',
            visible: !this.hiddenLegends.includes(dataset.name) ? true : 'legendonly',
            yaxis: 'y2' // Use secondary y-axis
          };

          traces.push(trace);
        });
      }

      return traces;
    },
    createLayout() {
      // Detect dark mode
      const isDarkMode = document.documentElement.classList.contains('dark-mode');
      const paperBgColor = isDarkMode ? '#2a2a2a' : '#ffffff';
      const plotBgColor = isDarkMode ? '#2a2a2a' : '#ffffff';
      const textColor = isDarkMode ? '#e0e0e0' : '#333333';
      const legendBgColor = isDarkMode ? 'rgba(42, 42, 42, 0.9)' : 'rgba(255, 255, 255, 0.8)';
      const legendBorderColor = isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)';
      const gridColor = isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(128, 128, 128, 0.2)';
      
      // Get labels to calculate x-axis range
      const labels = this.datasets.length > 0 && this.datasets[0].labels ? this.datasets[0].labels : [];
      const labelCount = labels.length;

      const layout = {
        paper_bgcolor: paperBgColor,
        plot_bgcolor: plotBgColor,
        title: {
          text: this.chartTitle,
          font: {
            size: 16,
            color: textColor
          }
        },
        xaxis: {
          title: {
            text: 'Time',
            font: {
              color: textColor
            },
            standoff: 20
          },
          showgrid: true,
          gridcolor: gridColor,
          tickangle: this.xAxisTickAngle,
          tickfont: {
            color: textColor
          },
          domain: [0, 1],
          type: labelCount > 0 ? 'category' : 'linear',
          categoryorder: labelCount > 0 ? 'array' : undefined,
          categoryarray: labelCount > 0 ? labels : undefined
        },
        yaxis: {
          type: this.yAxisType,
          title: {
            text: this.dataUnit || '',
            font: {
              size: 14,
              color: textColor
            },
            standoff: 10
          },
          showgrid: true,
          gridcolor: gridColor,
          tickfont: {
            color: textColor
          },
          range: this.yAxisMin !== null && this.yAxisMax !== null 
            ? [this.yAxisMin, this.yAxisMax] 
            : undefined
        },
        legend: {
          x: 0,
          y: 1,
          bgcolor: legendBgColor,
          bordercolor: legendBorderColor,
          borderwidth: 1,
          font: {
            color: textColor
          }
        },
        hovermode: 'x unified',
        hoverlabel: {
          bgcolor: isDarkMode ? 'rgba(42, 42, 42, 0.95)' : 'rgba(255, 255, 255, 0.95)',
          bordercolor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
          font: {
            color: textColor,
            size: 12
          },
          namelength: -1,
          align: 'left'
        },
        margin: {
          l: this.dataUnit ? 70 : 60,
          r: this.secondaryDatasets && this.secondaryDatasets.length > 0 ? 80 : 30,
          t: 50,
          b: (this.xAxisTickAngle === 270 || this.xAxisTickAngle === -90) ? 80 : 70,
          pad: 0
        },
        autosize: true,
        width: null,
        height: null
      };

      // Only add yaxis2 if we have secondary datasets
      if (this.secondaryDatasets && this.secondaryDatasets.length > 0) {
        const secondaryAxisType = this.secondaryDataUnit === '%' ? 'linear' : this.secondaryYAxisType;
        layout.yaxis2 = {
          type: secondaryAxisType,
          title: {
            text: this.secondaryDataUnit || '',
            font: {
              size: 14,
              color: textColor
            },
            standoff: 10
          },
          overlaying: 'y',
          side: 'right',
          showgrid: false,
          tickfont: {
            color: textColor
          },
          range: [0, 100]
        };
      }

      return layout;
    },
    setupEventHandlers() {
      if (!this.plotlyInstance) return;

      // Remove existing event listeners before adding new ones to prevent memory leaks
      this.removeEventHandlers();

      // Setup hover tooltip width adjustment
      this.setupHoverTooltipWidth();

      // Create and store legend click handler
      this.legendClickHandler = (data) => {
        // Prevent default legend click behavior
        // Get the clicked trace index
        const clickedTraceIndex = data.curveNumber;
        
        // Get all traces from the plot
        const plotData = this.plotlyInstance.data;
        
        if (clickedTraceIndex >= 0 && clickedTraceIndex < plotData.length) {
          const clickedTrace = plotData[clickedTraceIndex];
          
          // Toggle visibility
          const isCurrentlyVisible = clickedTrace.visible === true;
          const newVisibility = isCurrentlyVisible ? 'legendonly' : true;
          
          // Find shadow trace if this is a SoC trace
          // Shadow traces have the pattern "{originalName} (shadow)"
          // They also have showlegend: false and yaxis: 'y2'
          const shadowTraceName = clickedTrace.name + ' (shadow)';
          const shadowTraceIndex = plotData.findIndex(trace => 
            trace.name === shadowTraceName &&
            trace.showlegend === false &&
            trace.yaxis === 'y2'
          );
          
          // Prepare updates for both the clicked trace and its shadow (if exists)
          const updateIndices = [clickedTraceIndex];
          const visibilityArray = [newVisibility];
          
          if (shadowTraceIndex !== -1) {
            updateIndices.push(shadowTraceIndex);
            visibilityArray.push(newVisibility);
          }
          
          // Update the trace visibility using restyle
          // When updating multiple traces, pass array of indices and array of values
          Plotly.restyle(this.chartId, {
            visible: visibilityArray
          }, updateIndices).then(() => {
            // After restyle, get updated plot data to build hidden legends list
            const updatedPlotData = this.plotlyInstance.data;
            const hiddenLegends = [];
            
            updatedPlotData.forEach((trace) => {
              // Only include traces that are in the legend (showlegend !== false)
              // and are hidden, excluding shadow traces from the hidden legends list
              if ((trace.visible === 'legendonly' || trace.visible === false) && 
                  trace.showlegend !== false) {
                hiddenLegends.push(trace.name);
              }
            });
            
            // Emit the updated hidden legends list
            this.$emit('legends-updated', hiddenLegends);
          });
        }
        
        return false;
      };

      // Create and store legend double-click handler
      this.legendDoubleClickHandler = (data) => {
        // Prevent default legend double-click behavior
        return false;
      };

      // Add event listeners
      this.plotlyInstance.on('plotly_legendclick', this.legendClickHandler);
      this.plotlyInstance.on('plotly_legenddoubleclick', this.legendDoubleClickHandler);
      
      this.eventHandlersSetup = true;
    },
    removeEventHandlers() {
      if (!this.plotlyInstance) return;

      // Remove hover event handler
      if (this.hoverEventHandler) {
        this.plotlyInstance.removeListener('plotly_hover', this.hoverEventHandler);
        this.hoverEventHandler = null;
      }

      // Remove legend click handler
      if (this.legendClickHandler) {
        this.plotlyInstance.removeListener('plotly_legendclick', this.legendClickHandler);
        this.legendClickHandler = null;
      }

      // Remove legend double-click handler
      if (this.legendDoubleClickHandler) {
        this.plotlyInstance.removeListener('plotly_legenddoubleclick', this.legendDoubleClickHandler);
        this.legendDoubleClickHandler = null;
      }

      // Disconnect hover observer
      if (this.hoverObserver) {
        this.hoverObserver.disconnect();
        this.hoverObserver = null;
      }

      this.eventHandlersSetup = false;
    },
    updateZoom({ min, max }) {
      if (!this.plotlyInstance) return;
      
      const update = {
        'xaxis.range': [min, max]
      };
      
      Plotly.relayout(this.chartId, update);
    },
    resetZoom() {
      if (!this.plotlyInstance) return;
      
      Plotly.relayout(this.chartId, {
        'xaxis.autorange': true
      });
    },
    handleResize() {
      if (this.plotlyInstance) {
        Plotly.Plots.resize(this.chartId);
      }
    },
    setupHoverTooltipWidth() {
      if (!this.plotlyInstance) return;
      
      // Remove existing hover observer and event handler before creating new ones
      if (this.hoverObserver) {
        this.hoverObserver.disconnect();
        this.hoverObserver = null;
      }
      
      if (this.hoverEventHandler && this.plotlyInstance) {
        this.plotlyInstance.removeListener('plotly_hover', this.hoverEventHandler);
        this.hoverEventHandler = null;
      }
      
      // Use MutationObserver to watch for hover tooltip creation and adjust width
      this.hoverObserver = new MutationObserver((mutations) => {
        if (!this.plotlyInstance) return;
        
        const hoverTexts = this.plotlyInstance.querySelectorAll('.hovertext');
        hoverTexts.forEach((hoverText) => {
          // Set minimum width to 2x the default
          if (hoverText.style.minWidth !== '400px') {
            hoverText.style.minWidth = '400px';
            hoverText.style.width = 'auto';
            hoverText.style.maxWidth = 'none';
          }
          
          // Ensure name elements don't truncate
          const nameElements = hoverText.querySelectorAll('.name');
          nameElements.forEach((nameEl) => {
            nameEl.style.whiteSpace = 'nowrap';
            nameEl.style.overflow = 'visible';
            nameEl.style.textOverflow = 'clip';
            nameEl.style.display = 'inline-block';
          });
        });
      });

      // Observe the plotly container for changes
      if (this.plotlyInstance) {
        this.hoverObserver.observe(this.plotlyInstance, {
          childList: true,
          subtree: true,
          attributes: false
        });
      }

      // Also adjust on hover events
      this.hoverEventHandler = () => {
        if (!this.plotlyInstance) return;
        
        setTimeout(() => {
          if (!this.plotlyInstance) return;
          
          const hoverTexts = this.plotlyInstance.querySelectorAll('.hovertext');
          hoverTexts.forEach((hoverText) => {
            hoverText.style.minWidth = '400px';
            hoverText.style.width = 'auto';
            hoverText.style.maxWidth = 'none';
            
            const nameElements = hoverText.querySelectorAll('.name');
            nameElements.forEach((nameEl) => {
              nameEl.style.whiteSpace = 'nowrap';
              nameEl.style.overflow = 'visible';
              nameEl.style.textOverflow = 'clip';
            });
          });
        }, 10);
      };
      
      this.plotlyInstance.on('plotly_hover', this.hoverEventHandler);
    }
  }
};
</script>

<style scoped>
.plotly-chart-container {
  width: 100%;
  height: 500px;
  position: relative;
  overflow: hidden;
}

.plotly-chart-container > div {
  width: 100% !important;
  max-width: 100% !important;
}

.plotly-chart-container .js-plotly-plot {
  width: 100% !important;
  max-width: 100% !important;
}

@media (max-width: 750px) {
  .plotly-chart-container {
    height: 700px;
  }
}
</style>

<style>
/* Non-scoped styles for Plotly dynamically generated hover elements */
.plotly-chart-container .hoverlayer .hovertext,
.plotly-chart-container .hovertext,
div[id*="power-chart"] .hovertext,
div[id*="price-chart"] .hovertext,
div[id*="soc-chart"] .hovertext {
  min-width: 400px !important;
  width: auto !important;
  max-width: none !important;
}

.plotly-chart-container .hoverlayer .hovertext .name,
.plotly-chart-container .hovertext .name,
div[id*="power-chart"] .hovertext .name,
div[id*="price-chart"] .hovertext .name,
div[id*="soc-chart"] .hovertext .name {
  white-space: nowrap !important;
  overflow: visible !important;
  text-overflow: clip !important;
  display: inline-block !important;
  max-width: none !important;
}

/* Target the span elements inside hovertext */
.plotly-chart-container .hovertext > div,
.plotly-chart-container .hovertext > span {
  white-space: nowrap !important;
  overflow: visible !important;
}
</style>

